import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import { Container, Row, Col } from "react-bootstrap";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import apiService from "../../services/api";
import { formatRefundDateTime } from "./refundDateFormat";
import {
  buildSecurityDepositBreakdown,
  REFUND_STATUS_CODE,
} from "./editRefundDepositBreakdown";
import SecurityDepositBreakdownCard from "../../components/common/SecurityDepositBreakdownCard";
import {
  DELETE_REASON_CATEGORY,
  findDeleteReasonOtherCodeValue,
  mapDeleteReasonSelectOptions,
  resolveRefundDeleteReasonPayload,
  type CodeCommonOption,
} from "./refundDeleteReasonUtils";
import DeleteConfirmDialog from "../../components/common/DeleteConfirmDialog";
import useRBAC, { ROLES } from "../../hooks/useRBAC";
import { useNotification } from "../../contexts/NotificationContext";

function formatAuditActionUser(log: RefundAuditEntry): string {
  const name = String(log.actionusername ?? "").trim();
  if (name) return name;
  if (log.userid === 0) return "System";
  if (log.userid != null) return `User #${log.userid}`;
  return "N/A";
}

/** Audit entry when API returns `auditlist` */
export interface RefundAuditEntry {
  auditid?: number;
  categoryid?: number;
  description?: string;
  actionusername?: string;
  userid?: number;
  bookingid?: number;
  planid?: number;
  refundid?: number;
  planname?: string;
  enteredon?: string;
}

/** Single refund row from GetRefunds API */
export interface RefundApiRow {
  refundid: number;
  bookingmasterid: number;
  name: string;
  phonenumber: string;
  /** API proposed refund (shown as "Proposed Refundable Amount"). */
  refundableamount: number;
  /** API refundable / final amount (editable "Refundable Amount"). */
  proposedamount: number | null;
  ridecompletedate: string;
  paymentstatus: number;
  razorpayrefundid: string | null;
  refundinitiatedon: string | null;
  refundtype: string | null;
  bookingamount: number;
  /** Null when API omits payment method (e.g. static/mock payload). */
  paymentmethod: number | null;
  hubname: string | null;
  hub: string | null;
  auditlist?: RefundAuditEntry[];
  transactionid: string | null;
  refunddescription: string | null;
}

/** `location.state.data` may be one object or an array of API rows */
function resolveRefundRecordFromLocation(state: unknown): unknown {
  if (state == null || typeof state !== "object") return null;
  const s = state as { data?: unknown; row?: unknown };
  const payload = s.data ?? s.row;
  if (Array.isArray(payload)) {
    return payload[0] ?? null;
  }
  return payload ?? null;
}

function getRawRecord(navRow: unknown): Record<string, unknown> {
  if (navRow != null && typeof navRow === "object") {
    const o = navRow as Record<string, unknown>;
    if (o.rawItem != null && typeof o.rawItem === "object") {
      return o.rawItem as Record<string, unknown>;
    }
    return o;
  }
  return {};
}

/** Normalize list/detail navigation state (camelCase UI + optional rawItem) to API shape */
function normalizeRefundRow(navRow: unknown): RefundApiRow | null {
  if (navRow == null || typeof navRow !== "object") return null;
  const o = navRow as Record<string, unknown>;
  const raw = getRawRecord(navRow);

  const n = (v: unknown, fallback = 0) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : fallback;
  };

  const rideRaw =
    raw.ridecompletedate ?? o.rideCompleteDate ?? raw.ridecompletedate ?? "";
  const rideIso = String(rideRaw ?? "");

  const pmRaw = raw.paymentmethod ?? o.paymentMethod ?? o.paymentmethod;
  let paymentmethod: number | null;
  if (pmRaw === null || pmRaw === undefined || pmRaw === "") {
    paymentmethod = null;
  } else {
    const pv = Number(pmRaw);
    paymentmethod = Number.isFinite(pv) ? pv : null;
  }

  const auditRaw = raw.auditlist ?? o.auditlist;
  const auditlist = Array.isArray(auditRaw)
    ? auditRaw.map((entry) => {
        if (entry == null || typeof entry !== "object") return entry;
        const a = entry as Record<string, unknown>;
        return {
          ...a,
          actionusername: String(
            a.actionusername ?? a.actionUsername ?? a.action_user_name ?? "",
          ).trim(),
        } as RefundAuditEntry;
      })
    : undefined;

  return {
    refundid: n(raw.refundid ?? o.refundId, 0),
    bookingmasterid: n(raw.bookingmasterid ?? o.bookingMasterId, 0),
    name: String(raw.name ?? o.name ?? ""),
    phonenumber: String(raw.phonenumber ?? o.phoneNumber ?? ""),
    refundableamount: n(
      raw.refundableamount ?? o.proposedRefundAmount ?? o.refundableamount,
      0,
    ),
    proposedamount: (() => {
      const v =
        raw.proposedamount ?? raw.proposedAmount ?? o.refundableAmount ?? null;
      if (v == null || v === "") return null;
      const x = Number(v);
      return Number.isFinite(x) ? x : null;
    })(),
    ridecompletedate: rideIso,
    paymentstatus: n(raw.paymentstatus ?? o.paymentStatus, 0),
    razorpayrefundid: (raw.razorpayrefundid ?? o.razorpayRefundId ?? null) as
      | string
      | null,
    refundinitiatedon: (raw.refundinitiatedon ??
      o.refundInitiatedOn ??
      null) as string | null,
    refundtype: (raw.refundtype ?? o.refundType ?? null) as string | null,
    bookingamount: n(raw.bookingamount ?? o.bookingAmount, 0),
    paymentmethod,
    hubname: String(raw.hubname ?? o.hubname ?? ""),
    hub: String(raw.hub ?? o.hub ?? ""),
    auditlist,
    transactionid: String(raw.transactionid ?? o.transactionId ?? ""),
    refunddescription: (() => {
      const v =
        raw.refunddescription ?? raw.refundDescription ?? o.refunddescription;
      if (v == null || v === "") return null;
      const s = String(v).trim();
      return s !== "" ? s : null;
    })(),
  };
}

function formatRideDisplay(normalized: RefundApiRow, navRow: unknown): string {
  const o =
    navRow != null && typeof navRow === "object"
      ? (navRow as Record<string, unknown>)
      : {};
  const preformatted = o.rideCompleteDate;
  if (
    typeof preformatted === "string" &&
    preformatted.trim() !== "" &&
    !preformatted.includes("T")
  ) {
    return preformatted;
  }
  if (normalized.ridecompletedate) {
    return formatRefundDateTime(normalized.ridecompletedate);
  }
  return "N/A";
}

type RefundReasonOption = {
  codevalue?: string | number;
  codedisplayname?: string;
};

/** RefundReason "Other" — `codevalue` is typically `4`. */
function findRefundReasonOtherCodeValue(options: RefundReasonOption[]): string {
  const row = options.find(
    (o) =>
      String(o.codedisplayname ?? "")
        .trim()
        .toLowerCase() === "other",
  );
  if (row != null) {
    const v = String(row.codevalue ?? "").trim();
    if (v) return v;
  }
  return "4";
}

function parseReasonRefundIdCode(selectedCode: string): number {
  const code = selectedCode.trim();
  if (!code) return 0;
  const n = Number(code);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function buildUpdatePayload(
  base: RefundApiRow,
  raw: Record<string, unknown>,
  amount: number,
  refundType: string,
  /** RefundReason dropdown `codevalue`. */
  selectedReasonCode: string,
  /** Optional free-text remark → `updatereason`. */
  dialogRemark?: string,
) {
  const updatereason = dialogRemark != null ? String(dialogRemark).trim() : "";

  return {
    refundid: base.refundid,
    bookingmasterid: base.bookingmasterid,
    bookingplanid: Number(raw.bookingplanid ?? raw.bookingPlanId ?? 0) || 0,
    amount,
    updatereason,
    paymentid: Number(raw.paymentid ?? raw.paymentId ?? 0) || 0,
    paymentmethod: base.paymentmethod ?? 0,
    transactionid: String(raw.transactionid ?? raw.transactionId ?? ""),
    refundType,
    refundreasonid: parseReasonRefundIdCode(selectedReasonCode),
  };
}

function assertApiNotFailed(res: unknown, fallbackMessage: string): void {
  if (res == null || typeof res !== "object") return;
  const o = res as {
    isSuccess?: boolean;
    success?: boolean;
    message?: string;
    Message?: string;
  };
  if (o.isSuccess === false || o.success === false) {
    const msg =
      (typeof o.message === "string" && o.message.trim() !== ""
        ? o.message.trim()
        : null) ??
      (typeof o.Message === "string" && o.Message.trim() !== ""
        ? o.Message.trim()
        : null) ??
      fallbackMessage;
    throw new Error(msg);
  }
}

function extractApiMessage(res: unknown): string | null {
  if (res == null || typeof res !== "object") return null;
  const o = res as { message?: string; Message?: string };
  const m = o.message ?? o.Message;
  if (typeof m === "string" && m.trim() !== "") return m.trim();
  return null;
}

function formatRefundAmountDisplay(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  return `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const REFUND_ACTION_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.HUB_MANAGER,
  ROLES.HUB_CLUSTER_MANAGER,
] as const;

const EditRefundPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { hasAnyRole } = useRBAC();
  const canEditOrDeleteRefund = hasAnyRole([...REFUND_ACTION_ROLES]);

  const resolvedRecord = useMemo(
    () => resolveRefundRecordFromLocation(location.state),
    [location.state],
  );

  const normalized = useMemo(
    () => normalizeRefundRow(resolvedRecord),
    [resolvedRecord],
  );

  const issueCouponHeaderBlue = "rgba(22, 67, 135, 1)";
  const issueCouponSubmitYellow = "#FFEB3B";

  const issueCouponPrimaryButtonSx = {
    backgroundColor: issueCouponSubmitYellow,
    color: issueCouponHeaderBlue,
    fontWeight: 700,
    textTransform: "none" as const,
    px: 2.5,
    py: 1,
    minHeight: 40,
    borderRadius: 1,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#FDD835",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 235, 59, 0.5)",
      color: "rgba(22, 67, 135, 0.6)",
    },
  };

  const [editedAmount, setEditedAmount] = useState<string | number>("");
  const [amountError, setAmountError] = useState("");
  const originalRefundableSnapshotRef = useRef<number | null>(null);
  const apiProposedRefundAmountRef = useRef<number | null>(null);

  const [refundTypeDialogOpen, setRefundTypeDialogOpen] = useState(false);
  const [refundTypeOptions, setRefundTypeOptions] = useState<
    { codevalue?: string | number; codedisplayname?: string }[]
  >([]);
  const [selectedRefundType, setSelectedRefundType] = useState("");
  const [refundReasonOptions, setRefundReasonOptions] = useState<
    RefundReasonOption[]
  >([]);
  const [selectedRefundReason, setSelectedRefundReason] = useState("");
  const [refundUpdateRemark, setRefundUpdateRemark] = useState("");
  const [savingRefund, setSavingRefund] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRefundReasonOptions, setDeleteRefundReasonOptions] = useState<
    CodeCommonOption[]
  >([]);
  const [deleteRefundReasonCode, setDeleteRefundReasonCode] = useState("");
  const [deleteRefundRemark, setDeleteRefundRemark] = useState("");
  const [deleteRefundLoading, setDeleteRefundLoading] = useState(false);

  const refundReasonOtherCode = useMemo(
    () => findRefundReasonOtherCodeValue(refundReasonOptions),
    [refundReasonOptions],
  );

  const isOtherRefundReasonSelected = useMemo(() => {
    const code = selectedRefundReason.trim();
    return (
      code !== "" &&
      refundReasonOtherCode !== "" &&
      code === refundReasonOtherCode
    );
  }, [selectedRefundReason, refundReasonOtherCode]);

  const isRefundReasonComplete = useMemo(() => {
    if (selectedRefundReason.trim() === "") return false;
    if (isOtherRefundReasonSelected) return refundUpdateRemark.trim() !== "";
    return true;
  }, [selectedRefundReason, isOtherRefundReasonSelected, refundUpdateRemark]);

  const refundAmountSummary = useMemo((): {
    total: number;
    proposed: number | null;
    final: number | null;
  } => {
    const parsed = Number(editedAmount);
    const final = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    const bookingAmount = normalized?.bookingamount;
    const total = Number.isFinite(bookingAmount) ? bookingAmount : 0;
    const apiProposed =
      apiProposedRefundAmountRef.current ??
      normalized?.refundableamount ??
      null;
    const proposed =
      apiProposed != null && Number.isFinite(apiProposed) && apiProposed > 0
        ? apiProposed
        : null;
    return {
      total: total ?? 0,
      proposed,
      final,
    };
  }, [normalized?.bookingamount, normalized?.refundableamount, editedAmount]);

  const [cashMethodCodes, setCashMethodCodes] = useState<Set<number>>(
    () => new Set(),
  );

  /** codevalue string → display label */
  const [paymentMethodLabels, setPaymentMethodLabels] = useState<
    Record<string, string>
  >({});
  const [paymentStatusLabels, setPaymentStatusLabels] = useState<
    Record<string, string>
  >({});
  useEffect(() => {
    if (normalized) {
      apiProposedRefundAmountRef.current =
        Number.isFinite(normalized.refundableamount) &&
        normalized.refundableamount > 0
          ? normalized.refundableamount
          : null;
      if (originalRefundableSnapshotRef.current === null) {
        originalRefundableSnapshotRef.current = normalized.refundableamount;
      }
      const initialEditable =
        normalized.proposedamount != null
          ? normalized.proposedamount
          : normalized.refundableamount !== 0
            ? normalized.refundableamount
            : null;
      setEditedAmount(
        initialEditable != null && initialEditable !== 0
          ? String(initialEditable)
          : "",
      );
    }
  }, [normalized]);

  const securityDepositBreakdown = useMemo(() => {
    if (!normalized) return null;
    const raw = getRawRecord(resolvedRecord);
    const snapshot =
      originalRefundableSnapshotRef.current ?? normalized.refundableamount;
    return buildSecurityDepositBreakdown(
      raw,
      normalized,
      snapshot,
      editedAmount,
    );
  }, [normalized, resolvedRecord, editedAmount]);

  useEffect(() => {
    const fetchRefundTypes = async () => {
      try {
        const res: unknown =
          await apiService.getAllCodeCommonByCategory("RefundType");
        const data = Array.isArray(res)
          ? res
          : (res as { data?: unknown })?.data;
        setRefundTypeOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load refund types", err);
      }
    };
    void fetchRefundTypes();
  }, []);

  useEffect(() => {
    const fetchRefundReasons = async () => {
      try {
        const res: unknown =
          await apiService.getAllCodeCommonByCategory("RefundReason");
        const data = Array.isArray(res)
          ? res
          : (res as { data?: unknown })?.data;
        setRefundReasonOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load refund reasons", err);
      }
    };
    void fetchRefundReasons();
  }, []);

  useEffect(() => {
    const fetchDeleteReasons = async () => {
      try {
        const res: unknown = await apiService.getAllCodeCommonByCategory(
          DELETE_REASON_CATEGORY,
        );
        const data = Array.isArray(res)
          ? res
          : (res as { data?: unknown })?.data;
        setDeleteRefundReasonOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load delete reasons", err);
      }
    };
    void fetchDeleteReasons();
  }, []);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const res: unknown =
          await apiService.getAllCodeCommonByCategory("PaymentMethod");
        const data = Array.isArray(res)
          ? res
          : (res as { data?: unknown })?.data;
        const list = Array.isArray(data) ? data : [];
        const cash = new Set<number>();
        for (const opt of list as {
          codename?: string;
          codeName?: string;
          codedisplayname?: string;
          codevalue?: string | number;
        }[]) {
          const cn = String(opt.codename ?? opt.codeName ?? "").toLowerCase();
          const disp = String(opt.codedisplayname ?? "").toLowerCase();
          if (cn.includes("cash") || disp.includes("cash")) {
            const v = Number(opt.codevalue);
            if (Number.isFinite(v)) cash.add(v);
          }
        }
        setCashMethodCodes(cash);
        const methodLabels: Record<string, string> = {};
        for (const opt of list as {
          codevalue?: string | number;
          codedisplayname?: string;
        }[]) {
          const key = String(opt.codevalue ?? "");
          if (key) methodLabels[key] = String(opt.codedisplayname ?? key);
        }
        setPaymentMethodLabels(methodLabels);
      } catch (err) {
        console.error("Failed to load payment methods", err);
      }
    };
    void fetchPaymentMethods();
  }, []);

  useEffect(() => {
    const fetchPaymentStatuses = async () => {
      try {
        const res: unknown =
          await apiService.getAllCodeCommonByCategory("RefundStatus");
        const data = Array.isArray(res)
          ? res
          : (res as { data?: unknown })?.data;
        const list = Array.isArray(data) ? data : [];
        const labels: Record<string, string> = {};
        for (const opt of list as {
          codename?: string;
          codeName?: string;
          codedisplayname?: string;
          codevalue?: string | number;
        }[]) {
          const key = String(opt.codevalue ?? "");
          const codeName = String(
            opt.codedisplayname ?? opt.codedisplayname ?? "",
          ).toLowerCase();
          const label =
            codeName === "refund"
              ? "Inprogress"
              : String(opt.codedisplayname ?? key);
          if (key) labels[key] = label;
        }
        setPaymentStatusLabels(labels);
      } catch (err) {
        console.error("Failed to load payment statuses", err);
      }
    };
    void fetchPaymentStatuses();
  }, []);

  const isCashPayment = useCallback((): boolean => {
    if (!normalized || normalized.paymentmethod === null) return false;
    return cashMethodCodes.has(normalized.paymentmethod);
  }, [normalized, cashMethodCodes]);

  /** Amount edit + Proceed only for Added (1) or Failed (4). */
  const canEditRefundAmountAndProceed = useMemo(() => {
    if (!normalized) return false;
    const s = normalized.paymentstatus;
    return s === REFUND_STATUS_CODE.ADDED || s === REFUND_STATUS_CODE.FAILED;
  }, [normalized]);

  /** Delete only for Added (1), same as refund list. */
  const canDeleteRefund = useMemo(() => {
    if (!normalized || !canEditOrDeleteRefund) return false;
    return normalized.paymentstatus === REFUND_STATUS_CODE.ADDED;
  }, [normalized, canEditOrDeleteRefund]);

  const deleteRefundReasonSelectOptions = useMemo(
    () => mapDeleteReasonSelectOptions(deleteRefundReasonOptions),
    [deleteRefundReasonOptions],
  );

  const deleteRefundReasonOtherCode = useMemo(
    () => findDeleteReasonOtherCodeValue(deleteRefundReasonOptions),
    [deleteRefundReasonOptions],
  );

  const validateAmount = (value: string, bookingCap: number) => {
    if (!value) {
      setAmountError("");
      return;
    }
    const amount = Number(value);
    if (amount > bookingCap) {
      setAmountError(
        `Refundable amount cannot be greater than booking amount (${bookingCap})`,
      );
    } else if (amount < 0) {
      setAmountError("Amount cannot be negative");
    } else {
      setAmountError("");
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEditedAmount(value);
    if (normalized) {
      validateAmount(value, normalized.bookingamount);
    }
  };

  const runUpdateRefund = async (
    refundType: string,
    selectedReasonCode = "",
    dialogRemark?: string,
  ) => {
    if (!normalized) {
      throw new Error("Refund data is not available.");
    }
    const raw = getRawRecord(resolvedRecord);
    const amt = Number(editedAmount);
    if (!Number.isFinite(amt) || amt < 0) {
      throw new Error("Enter a valid amount.");
    }

    const payload = buildUpdatePayload(
      normalized,
      raw,
      amt,
      refundType,
      selectedReasonCode,
      dialogRemark,
    );
    const res = await apiService.updateRefund(payload);
    assertApiNotFailed(res, "Update refund failed.");
    return res;
  };

  const handleProceed = async () => {
    if (!normalized || !canEditRefundAmountAndProceed || amountError) return;

    const amt = Number(editedAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setAmountError("Enter a valid amount");
      return;
    }

    setRefundTypeDialogOpen(true);
  };

  const closeRefundTypeDialog = () => {
    setRefundTypeDialogOpen(false);
    setSelectedRefundType("");
    setSelectedRefundReason("");
    setRefundUpdateRemark("");
  };

  const handleSaveRefund = async () => {
    if (!normalized) return;
    if (!selectedRefundReason.trim()) {
      showNotification("Please select a reason.", "error");
      return;
    }
    const remarkTrim = refundUpdateRemark.trim();
    if (isOtherRefundReasonSelected && remarkTrim === "") {
      showNotification("Remark is required when reason is Other.", "error");
      return;
    }
    const cashPayment = isCashPayment();
    if (!cashPayment && !selectedRefundType.trim()) {
      showNotification("Please select a refund type.", "error");
      return;
    }
    const refundTypeForUpdate = cashPayment
      ? String(normalized.refundtype ?? "")
      : selectedRefundType;
    try {
      setSavingRefund(true);
      let initRes: unknown;
      if (!cashPayment) {
        const initPayload = {
          refundid: normalized.refundid,
          refundType: selectedRefundType,
          refundedamount: Number(editedAmount),
          remark: remarkTrim,
        };
        initRes = await apiService.initiateRefundProcessNew(initPayload);
        assertApiNotFailed(initRes, "Initiate refund process failed.");
      }

      const updateRes = await runUpdateRefund(
        refundTypeForUpdate,
        selectedRefundReason,
        remarkTrim,
      );

      closeRefundTypeDialog();
      showNotification(
        extractApiMessage(updateRes) ??
          (initRes != null ? extractApiMessage(initRes) : null) ??
          (cashPayment
            ? "Refund updated successfully."
            : "Refund processed successfully."),
        "success",
      );
      navigate("/refund-management");
    } catch (err: unknown) {
      console.error("Failed to process refund", err);
      const msg =
        err instanceof Error && err.message.trim() !== ""
          ? err.message
          : "Failed to process refund.";
      showNotification(msg, "error");
    } finally {
      setSavingRefund(false);
    }
  };

  const openDeleteRefundDialog = () => {
    setDeleteRefundReasonCode("");
    setDeleteRefundRemark("");
    setDeleteDialogOpen(true);
  };

  const cancelDeleteRefund = () => {
    if (deleteRefundLoading) return;
    setDeleteDialogOpen(false);
    setDeleteRefundReasonCode("");
    setDeleteRefundRemark("");
  };

  const confirmDeleteRefund = async () => {
    if (!normalized) return;
    const resolved = resolveRefundDeleteReasonPayload(
      deleteRefundReasonSelectOptions,
      deleteRefundReasonOtherCode,
      deleteRefundReasonCode,
      deleteRefundRemark,
    );
    if (!resolved.ok) {
      showNotification(resolved.error, "error");
      return;
    }
    const refundId = normalized.refundid;
    if (refundId == null || String(refundId).trim() === "") {
      showNotification("Invalid refund id.", "error");
      return;
    }
    setDeleteRefundLoading(true);
    try {
      const res = (await apiService.deleteRefund({
        refundid: refundId,
        deletereason: resolved.deletereason,
        deleterefundreasonid: Number(deleteRefundReasonCode),
      })) as {
        isSuccess?: boolean;
        message?: string;
      };
      if (res && typeof res === "object" && res.isSuccess === false) {
        throw new Error(
          typeof res.message === "string" && res.message.trim() !== ""
            ? res.message
            : "Delete refund failed.",
        );
      }
      const msg =
        typeof res?.message === "string" && res.message.trim() !== ""
          ? res.message.trim()
          : "Refund deleted.";
      showNotification(msg, "success");
      setDeleteDialogOpen(false);
      setDeleteRefundReasonCode("");
      setDeleteRefundRemark("");
      navigate("/refund-management", { state: { shouldRefetch: true } });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to delete refund.";
      showNotification(message, "error");
    } finally {
      setDeleteRefundLoading(false);
    }
  };

  if (!normalized) {
    return (
      <Container fluid className="pb-5">
        <Paper className="p-4">
          <Typography>No refund data. Go back to the list.</Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate("/refund-management")}>
            Back to refunds
          </Button>
        </Paper>
      </Container>
    );
  }

  const rideLabel = formatRideDisplay(normalized, resolvedRecord);

  const paymentStatusKey = String(normalized.paymentstatus ?? "");
  const paymentStatusDisplay =
    paymentStatusLabels[paymentStatusKey] ?? (paymentStatusKey || "—");

  const paymentMethodKey =
    normalized.paymentmethod === null ? "" : String(normalized.paymentmethod);
  const paymentMethodDisplay =
    normalized.paymentmethod === null
      ? "—"
      : (paymentMethodLabels[paymentMethodKey] ?? paymentMethodKey ?? "—");

  const showRefundIdField = [1, 2, 3, 4].includes(normalized.paymentstatus);

  const detailFields: { label: string; value: string | number }[] = [];
  if (showRefundIdField) {
    detailFields.push({
      label: "Refund ID",
      value: normalized.refundid ?? "N/A",
    });
  }
  detailFields.push(
    { label: "Booking ID", value: normalized.bookingmasterid || "N/A" },
    {
      label: "Transaction ID",
      value: normalized?.transactionid?.includes("cash")
        ? "Cash"
        : (normalized?.transactionid ?? "N/A"),
    },
    { label: "User Name", value: normalized.name || "N/A" },
    { label: "Mobile Number", value: normalized.phonenumber || "N/A" },
    { label: "Refund Status", value: paymentStatusDisplay },
    {
      label: "Payment method",
      value: paymentMethodDisplay,
    },
    {
      label: "Ride Completion Date",
      value: rideLabel,
    },
    { label: "Hub", value: normalized.hubname ?? normalized.hub ?? "N/A" },
    {
      label: "Booking Amount",
      value: normalized.bookingamount ?? "N/A",
    },
    {
      label: "Proposed Refundable Amount",
      value: securityDepositBreakdown?.proposedRefundAmount ?? "N/A",
    },
    {
      label: "Refund Remark",
      value: normalized.refunddescription?.trim() || "N/A",
    },
  );

  const auditLogs = normalized.auditlist ?? [];

  /** Space so scrollable content clears the fixed action bar */
  const stickyActionBarSx = {
    pb: { xs: "88px", sm: "96px" },
  };

  return (
    <>
      <Box sx={stickyActionBarSx}>
        <Container fluid>
          <Paper className="mb-4 shadow-sm border-0 rounded-3">
            <Box className="px-4 pt-4 pb-4">
              <Typography
                variant="h6"
                className="fw-bold mb-3"
                sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
              >
                Edit Refund Details
              </Typography>

              <Box
                sx={{
                  height: "1px",
                  backgroundColor: "#e9ecef",
                  mb: 3,
                }}
              />

              <Row className="g-4">
                {detailFields.map((field, idx) => (
                  <Col xs={12} sm={6} md={4} key={idx}>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6c757d",
                          mb: 0.5,
                          fontSize: "0.85rem",
                          fontWeight: 500,
                        }}
                      >
                        {field.label}
                      </Typography>

                      <Typography
                        variant="body1"
                        className="fw-bold"
                        sx={{
                          color: "#495057",
                          fontSize: "0.95rem",
                          wordBreak: "break-word",
                        }}
                      >
                        {field.value}
                      </Typography>
                    </Box>
                  </Col>
                ))}

                <Col xs={12} sm={6} md={4}>
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6c757d",
                        mb: 0.5,
                        fontSize: "0.85rem",
                        fontWeight: 500,
                      }}
                    >
                      Refundable Amount
                    </Typography>

                    <TextField
                      type="number"
                      value={editedAmount}
                      onChange={handleAmountChange}
                      fullWidth
                      size="small"
                      disabled={!canEditRefundAmountAndProceed}
                      inputProps={{
                        min: 0,
                        step: 1,
                      }}
                      placeholder="Enter amount"
                      error={!!amountError}
                      helperText={amountError}
                    />
                  </Box>
                </Col>
              </Row>
            </Box>
          </Paper>

          {securityDepositBreakdown ? (
            <SecurityDepositBreakdownCard
              breakdown={securityDepositBreakdown}
            />
          ) : null}

          <Accordion
            defaultExpanded={false}
            className="mb-4"
            sx={{
              borderRadius: 2,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon sx={{ color: "rgba(22, 67, 135, 1)" }} />
              }
              sx={{
                px: 2.5,
                py: 1.5,
                "&:hover": {
                  backgroundColor: "rgba(22, 67, 135, 0.02)",
                },
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{ width: "100%" }}
              >
                <HistoryIcon
                  sx={{ color: "rgba(22, 67, 135, 1)", fontSize: 26 }}
                />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(22, 67, 135, 1)",
                      fontSize: 18,
                      fontWeight: 600,
                    }}
                  >
                    Refund activity history
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Audit trail of refund-related events
                    {auditLogs.length > 0 &&
                      ` · ${auditLogs.length} ${
                        auditLogs.length === 1 ? "entry" : "entries"
                      }`}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
              {auditLogs.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", py: 2 }}
                >
                  No activity records found for this refund.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {auditLogs.map((log, index) => {
                    const cat = Number(log.categoryid);
                    const chipLabel =
                      cat === 1
                        ? "Booking / plan"
                        : cat === 2
                          ? "Refund"
                          : "Update";
                    const isRefundCat = cat === 2;
                    const isBookingCat = cat === 1;
                    return (
                      <Paper
                        key={
                          log.auditid != null
                            ? `audit-${log.auditid}`
                            : `audit-${index}-${log.enteredon ?? ""}`
                        }
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          backgroundColor:
                            index % 2 === 0 ? "#fafafa" : "#ffffff",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            borderColor: "rgba(22, 67, 135, 0.25)",
                          },
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          flexWrap="wrap"
                          gap={1}
                          mb={1.5}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                          >
                            <Chip
                              size="small"
                              label={chipLabel}
                              sx={{
                                fontWeight: 600,
                                fontSize: 11,
                                backgroundColor: isBookingCat
                                  ? "rgba(227, 242, 253, 1)"
                                  : isRefundCat
                                    ? "rgba(232, 245, 233, 1)"
                                    : "rgba(245, 245, 245, 1)",
                                color: "rgba(22, 67, 135, 1)",
                              }}
                            />
                            {log.planname ? (
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                Plan: {log.planname}
                              </Typography>
                            ) : null}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", fontSize: 12 }}
                          >
                            {formatRefundDateTime(log.enteredon || "")}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.primary",
                            lineHeight: 1.65,
                            fontSize: 14,
                          }}
                        >
                          {log.description || "—"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            mt: 1,
                          }}
                        >
                          User ID:{" "}
                          {log.userid != null ? String(log.userid) : "N/A"} ·
                          User Name: {formatAuditActionUser(log)}
                          {log.bookingid != null
                            ? ` · Booking #${log.bookingid}`
                            : ""}
                          {log.refundid != null
                            ? ` · Refund #${log.refundid}`
                            : ""}
                          {log.auditid != null
                            ? ` · Audit #${log.auditid}`
                            : ""}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Container>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2.5,
            pb: 2,
            pr: 0.5,
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Box></Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
            }}
          >
            {" "}
            {canEditRefundAmountAndProceed ? (
              <Button
                variant="contained"
                disabled={!!amountError || savingRefund || deleteRefundLoading}
                onClick={() => void handleProceed()}
                sx={{
                  backgroundColor: "rgba(13,44,90,1)",
                  minWidth: "130px",
                  "&:disabled": { backgroundColor: "rgba(13,44,90,0.5)" },
                }}
              >
                {savingRefund ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} color="inherit" /> Processing...
                  </Box>
                ) : (
                  "Proceed"
                )}
              </Button>
            ) : null}
            {canDeleteRefund ? (
              <Button
                variant="outlined"
                color="error"
                disabled={savingRefund || deleteRefundLoading}
                onClick={openDeleteRefundDialog}
                sx={{ minWidth: "100px", fontWeight: 600 }}
              >
                Delete
              </Button>
            ) : null}
            <Button
              variant="outlined"
              disabled={deleteRefundLoading}
              onClick={() => navigate(-1)}
              sx={{
                borderColor: "rgba(13,44,90,1)",
                color: "rgba(13,44,90,1)",
                "&:hover": {
                  borderColor: "rgba(13,44,90,1)",
                  backgroundColor: "rgba(13,44,90,0.04)",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={refundTypeDialogOpen}
        onClose={!savingRefund ? closeRefundTypeDialog : undefined}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            m: 0,
            py: 1.75,
            px: 2,
            backgroundColor: issueCouponHeaderBlue,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 700,
            fontSize: "1.125rem",
          }}
        >
          {isCashPayment() ? "Update Refund" : "Select Refund Type"}
          <IconButton
            type="button"
            onClick={closeRefundTypeDialog}
            disabled={savingRefund}
            size="small"
            aria-label="Close"
            sx={{
              color: "#fff !important",
              bgcolor: "rgba(255,255,255,0.2)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pb: 2, pt: 3, position: "relative" }}>
          <Box
            mt={2}
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: "rgba(22, 67, 135, 0.06)",
              border: "1px solid rgba(22, 67, 135, 0.12)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 1.5,
                color: issueCouponHeaderBlue,
              }}
            >
              Information
            </Typography>
            {(
              [
                {
                  label: "Total Amount",
                  value: formatRefundAmountDisplay(refundAmountSummary.total),
                },
                {
                  label: "Proposed Refundable Amount",
                  value: formatRefundAmountDisplay(
                    refundAmountSummary.proposed,
                  ),
                },
                {
                  label: "Final Refund Amount",
                  value: formatRefundAmountDisplay(refundAmountSummary.final),
                },
              ] as const
            ).map((row) => (
              <Box
                key={row.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 2,
                  py: 0.75,
                  "&:not(:last-child)": {
                    borderBottom: "1px solid rgba(22, 67, 135, 0.08)",
                  },
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>
          {!isCashPayment() ? (
            <Box mt={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Refund Type"
                value={selectedRefundType}
                onChange={(e) => setSelectedRefundType(e.target.value)}
                disabled={savingRefund}
              >
                {refundTypeOptions.map((opt, idx) => {
                  const val = opt.codevalue;
                  const label = opt.codedisplayname;
                  return (
                    <MenuItem key={idx} value={val}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Box>
          ) : null}
          <Box mt={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Select reason"
              value={selectedRefundReason}
              onChange={(e) => setSelectedRefundReason(e.target.value)}
              disabled={savingRefund}
            >
              <MenuItem value="" disabled>
                Select reason
              </MenuItem>
              {refundReasonOptions.map((opt, idx) => {
                const val = String(opt.codevalue ?? "");
                const label = opt.codedisplayname ?? val;
                return (
                  <MenuItem key={`${val}-${idx}`} value={val}>
                    {label}
                  </MenuItem>
                );
              })}
            </TextField>
          </Box>
          <Box mt={2}>
            <TextField
              fullWidth
              size="small"
              label={
                isOtherRefundReasonSelected
                  ? "Remark (required)"
                  : "Remark (optional)"
              }
              placeholder="Enter remark for this refund update"
              value={refundUpdateRemark}
              onChange={(e) => setRefundUpdateRemark(e.target.value)}
              disabled={savingRefund}
              required={isOtherRefundReasonSelected}
              error={
                isOtherRefundReasonSelected && refundUpdateRemark.trim() === ""
              }
              helperText={
                isOtherRefundReasonSelected && refundUpdateRemark.trim() === ""
                  ? "Remark is required when reason is Other."
                  : undefined
              }
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 1,
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            sx={{
              color: issueCouponHeaderBlue,
              fontWeight: 600,
              textTransform: "none",
              border: "1px solid rgba(22, 67, 135, 0.6)",
            }}
            onClick={closeRefundTypeDialog}
            disabled={savingRefund}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleSaveRefund()}
            disabled={
              savingRefund ||
              !isRefundReasonComplete ||
              (!isCashPayment() && !selectedRefundType)
            }
            sx={issueCouponPrimaryButtonSx}
          >
            {savingRefund ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        message={`Delete refund for booking ${normalized.bookingmasterid}? This cannot be undone.`}
        requireDeleteReason
        deleteReasonLabel="Delete reason"
        {...(deleteRefundReasonSelectOptions.length > 0
          ? {
              deleteReasonOptions: deleteRefundReasonSelectOptions,
              selectedDeleteReasonCode: deleteRefundReasonCode,
              onDeleteReasonCodeChange: setDeleteRefundReasonCode,
              deleteRemark: deleteRefundRemark,
              onDeleteRemarkChange: setDeleteRefundRemark,
              deleteReasonOtherCodeValue: deleteRefundReasonOtherCode,
            }
          : {
              deleteReason: deleteRefundRemark,
              onDeleteReasonChange: setDeleteRefundRemark,
            })}
        loading={deleteRefundLoading}
        onCancel={cancelDeleteRefund}
        onConfirm={() => void confirmDeleteRefund()}
      />
    </>
  );
};

export default EditRefundPage;
